import type { CSSProperties, ReactNode } from 'react';

import { CubesField } from './CubesField.js';
import { BrandMark } from './Brand.js';

/**
 * Casca das telas de credencial: login, recuperação, nova senha e a troca
 * obrigatória do primeiro acesso.
 *
 * Duas colunas: à esquerda o painel institucional, maior, com o campo de cubos;
 * à direita o formulário, na largura de leitura. A ordem não é estética — quem
 * lê da esquerda para a direita encontra primeiro **onde está** e depois **o que
 * fazer**, e o que fazer fica do lado do gesto.
 *
 * **Abaixo de `lg` o painel some por inteiro.** Num celular ele roubaria a
 * altura do teclado virtual, e o campo de cubos custaria bateria e processador
 * para decorar uma tela onde só cabe o formulário.
 */
/** O que o sistema faz, em uma linha. Sem "públicas": nem toda obra é. */
const SLOGAN = 'Acompanhamento e fiscalização de obras';

export function AuthLayout({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  /**
   * Instrução da tela. Ausente no login, onde o painel já diz o que é o
   * sistema e repetir seria dizer duas vezes a mesma coisa na mesma dobra.
   */
  description?: string | undefined;
  children: ReactNode;
  footer?: ReactNode | undefined;
}) {
  return (
    <div className="grid min-h-dvh bg-card lg:grid-cols-[1.2fr_minmax(400px,0.8fr)]">
      <InstitutionalPanel />

      <div className="flex flex-col px-5 py-10">
        <main className="flex flex-1 flex-col items-center justify-center">
          <div className="w-full max-w-[368px]">
            {/* No celular não há painel: a marca e o slogan reaparecem aqui. */}
            <div className="mb-9 flex flex-col items-center gap-2.5 text-center lg:hidden">
              <BrandMark size={52} />
              <p className="text-[19px] font-semibold tracking-tight">Obras Hoje</p>
              <p className="text-[13px] text-muted-foreground">{SLOGAN}</p>
            </div>

            <h1 className="text-[24px] font-semibold tracking-tight">{title}</h1>
            {description ? (
              <p className="mt-1.5 text-[13px] text-muted-foreground">{description}</p>
            ) : null}

            <div className="mt-7">{children}</div>

            {footer ? <div className="mt-6 text-[13px] text-muted-foreground">{footer}</div> : null}
          </div>
        </main>

        <Copyright />
      </div>
    </div>
  );
}

/**
 * Autoria do sistema.
 *
 * Fica na coluna do formulário, e não no painel, por dois motivos: é a única
 * das duas que existe em toda largura de tela, e crédito de fabricante é nota
 * de rodapé — no painel disputaria a dobra com a marca do produto.
 */
function Copyright() {
  return (
    <p className="mt-10 text-center text-[12px] text-muted-foreground">
      © {new Date().getFullYear()}{' '}
      <a
        href="https://adaptta.com.br"
        target="_blank"
        rel="noreferrer noopener"
        className="font-medium transition-colors hover:text-foreground"
      >
        Adaptta
      </a>
    </p>
  );
}

/**
 * Painel escuro com o campo de cubos.
 *
 * A cor sai da própria interface, um degrau acima do fundo: face `#24242c`
 * sobre `#18181b`, aresta em branco a 16%. A referência (reactbits) vem com
 * face quase preta e aresta branca cheia — num painel institucional aquilo lê
 * como grade de planilha, e num painel escuro a aresta cheia vira listra.
 *
 * Foram precisas duas tentativas para achar o ponto: a 8% a aresta sumia e os
 * cubos viravam manchas; cheia, gritava mais que o formulário ao lado.
 *
 * **Laranja fica de fora.** É a cor de sinal do sistema (design.md §1) — marca a
 * ação primária e o que exige atenção. Gastá-la para decorar o fundo da tela de
 * entrada tiraria dela exatamente o que a torna útil nas outras telas. A
 * ondulação do clique clareia o cinza, e é o bastante para o toque ter resposta.
 */
function InstitutionalPanel() {
  const cores = {
    '--cube-face': '#24242c',
    '--cube-edge': 'rgba(255, 255, 255, 0.16)',
    '--cube-face-ripple': '#3a3a45',
    '--cube-edge-ripple': 'rgba(255, 255, 255, 0.34)',
  } as CSSProperties;

  return (
    <aside
      className="relative hidden overflow-hidden bg-foreground text-white lg:flex lg:flex-col lg:justify-end"
      style={cores}
    >
      <CubesField />

      {/*
        Véu só sob o texto, da base para cima e morrendo antes do meio.

        Todo o conteúdo do painel fica numa zona só, embaixo. A marca já esteve
        no topo e cruzava a aresta de um cubo: a palavra piscava conforme a
        grade se inclinava, e o véu que resolveria isso comeria o campo
        justamente onde ele tem mais o que mostrar.

        O gradiente é do próprio fundo, então não introduz cor nova.
      */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[64%]"
        style={{
          background:
            'linear-gradient(to top, var(--color-foreground) 18%, rgba(24,24,27,0.88) 52%, rgba(24,24,27,0) 100%)',
        }}
      />

      {/*
        Marca e slogan, e nada mais.

        O nome do município saiu daqui: o sistema atende mais de um, e a tela de
        entrada é a única que ninguém abre já sabendo qual é o seu. Quem se
        identifica é o produto; o órgão aparece depois, na escolha do ambiente e
        na barra lateral.
      */}
      <div className="relative p-12">
        <div className="flex items-center gap-3.5">
          <BrandMark size={54} inverted />
          <span className="text-[30px] font-semibold tracking-tight">Obras Hoje</span>
        </div>

        {/*
          Slogan é subtítulo da marca, não manchete: menor que o nome que ele
          acompanha, em peso normal e num branco recuado. Maior que "Obras Hoje"
          — como esteve — invertia a hierarquia, e a frase passava a competir
          com o nome do produto em vez de qualificá-lo.

          `whitespace-nowrap` porque uma linha só é o que faz ler como assinatura;
          quebrada em duas, vira parágrafo curto.
        */}
        <p className="mt-3.5 text-[17px] whitespace-nowrap text-white/50">{SLOGAN}</p>
      </div>
    </aside>
  );
}
